import { withDefaults } from '@warp-drive/legacy/model/migration-support';

/**
 * A Payment Operations rate ruling, recorded as its own event (tio-api#8187 / #10513) — read-only.
 *
 * This is what keeps a ruling answerable long after it was made: who ruled the pair, what they
 * ordained, the numbers they were shown, whether they chose to move the reserve, and — when a
 * batch only partly succeeded — which usages did not land. Written by the ruling service or not
 * at all; the API refuses both creation and editing, because editing one would rewrite the cover
 * choice that explains every row the ruling touched.
 */
const PcsRateRulingBatchSchema = withDefaults({
  type: 'pcs-rate-ruling-batch',
  fields: [
    {
      kind: 'attribute',
      name: 'program',
    },
    {
      kind: 'attribute',
      name: 'ruledRole',
    },
    {
      // Null when only the role was ruled — a complete answer for a pair whose swipes cannot
      // divide. Zero is an ordained no-fee merchant, which is not the same as null.
      kind: 'attribute',
      name: 'ruledCombinedRate',
    },
    {
      // APPLIED, or PARTIAL when some usages failed to carve. A partial batch is not a rollback:
      // failures are collected here rather than unwinding a decision a human already made.
      kind: 'attribute',
      name: 'state',
    },
    {
      // 'record-only' | 'record-and-cover' — whether this ruling also moved the reserve. Never
      // defaulted server-side, and persisted here so "why is this row uncovered?" stays
      // answerable months later.
      kind: 'attribute',
      name: 'coverMode',
    },
    {
      kind: 'attribute',
      name: 'candidateUsageCount',
    },
    {
      // Lower than the candidate count when a usage was skipped or failed; skippedLog and
      // errorsLog say which, so the gap is explainable from the record alone.
      kind: 'attribute',
      name: 'carvedUsageCount',
    },
    {
      kind: 'attribute',
      name: 'totalFeeAmount',
    },
    {
      // Digest of the plan the operator previewed. Apply refused a mismatch, so this is proof the
      // numbers signed off on are the numbers applied.
      kind: 'attribute',
      name: 'planFingerprint',
    },
    {
      kind: 'attribute',
      name: 'appliedAt',
    },
    {
      kind: 'attribute',
      name: 'errorsLog',
    },
    {
      // Usages the ruling deliberately left alone — e.g. one the live path carved concurrently.
      // Not failures.
      kind: 'attribute',
      name: 'skippedLog',
    },
    {
      // The house audit stamp: who ruled.
      kind: 'attribute',
      name: 'createdBy',
    },
    {
      kind: 'attribute',
      name: 'createdAt',
    },
    {
      kind: 'attribute',
      name: 'updatedAt',
    },
    {
      kind: 'belongsTo',
      name: 'pcsMerchantNetworkMerchant',
      type: 'pcs-merchant-network-merchant',
      options: { async: false, inverse: null },
    },
    {
      // The pair that received the same rate. Its role was ruled separately, or not at all.
      kind: 'belongsTo',
      name: 'counterpartMerchantNetworkMerchant',
      type: 'pcs-merchant-network-merchant',
      options: { async: false, inverse: null },
    },
    {
      // The worklist rows this ruling answered.
      kind: 'hasMany',
      name: 'pcsRateProposals',
      type: 'pcs-rate-proposal',
      options: { async: false, inverse: null },
    },
  ],
});

export default PcsRateRulingBatchSchema;
